import { createTRPCNuxtClient, httpBatchLink } from 'trpc-nuxt/client';
import type { RequestInitEsque } from '@trpc/client/dist/internals/types';
// @ts-expect-error no types package available
import nuxtStorage from 'nuxt-storage';
import type { AppRouter } from '@rollouter/backend';
import { NUXT_PUBLIC_BASE_URL_API } from '@/config/environment.config';
import { useUserStore, refreshAccessToken } from '@/stores/user';
import { useRuntimeConfig } from '#imports';
import { ErrorType } from '~/types/error';

export default defineNuxtPlugin(() => {
  /**
   * createTRPCNuxtClient adds a `useQuery` composable
   * built on top of `useAsyncData`.
   */
  const runtimeConfig = useRuntimeConfig();
  const route = useRoute();
  const client = createTRPCNuxtClient<AppRouter>({
    links: [
      httpBatchLink({
        url: runtimeConfig.public.baseUrlApi ?? NUXT_PUBLIC_BASE_URL_API,
        headers: () => {
          const accessToken = nuxtStorage.localStorage.getData('accessToken');
          return {
            Authorization: `Bearer ${accessToken}`,
          };
        },
        fetch: async function fetcher(
          info: RequestInfo | URL,
          options: RequestInit | RequestInitEsque | undefined
        ): Promise<Response> {
          const userStore = useUserStore();
          const response = await fetch(info, options);

          if (response.status === 401) {
            const refreshToken = userStore.refreshToken;
            if (!refreshToken && route.name !== 'index') {
              navigateTo({ path: '/', query: { 'session-expired': 'true' } });
              return response;
            }

            if (!refreshToken) {
              throw new Error(ErrorType.NO_PERMISSION);
            }

            try {
              const refreshAccessTokenResponse =
                await refreshAccessToken(refreshToken);

              if (!refreshAccessTokenResponse) {
                throw new Error(ErrorType.NO_PERMISSION);
              }

              return await fetch(info, {
                ...options,
                headers: {
                  ...options?.headers,
                  Authorization: `Bearer ${refreshAccessTokenResponse.newAccessToken}`,
                },
              });
            } catch (error) {
              if (route.name !== 'index') {
                navigateTo({ path: '/', query: { 'session-expired': 'true' } });
              }
              return response;
            }
          }
          return response;
        },
      }),
    ],
  });
  return {
    provide: {
      client,
    },
  };
});

